<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Confirm Your Order</h3>
        <button 
          class="close-button" 
          @click="$emit('cancel')" 
          v-if="!loading"
        >&times;</button>
      </div>
      
      <div class="modal-body">
        <div class="confirmation-content">
          <div class="order-details">
            <div class="product-row">
              <span>Content Commander Challenge</span>
              <span>${{ (basePrice / 100).toFixed(2) }}</span>
            </div>
            
            <div v-if="includeUpsell" class="product-row upsell-row">
              <div class="product-name">
                <i class="fas fa-graduation-cap"></i>
                <span>Full AI Captains Academy - All 9 Courses</span>
              </div>
              <span>${{ (upsellPrice / 100).toFixed(2) }}</span>
            </div>
            
            <div class="total-row">
              <strong>Total</strong>
              <strong>${{ (total / 100).toFixed(2) }}</strong>
            </div>
          </div>
          
          <div class="confirmation-message">
            <p>By clicking "Complete Purchase", your card will be charged and you'll receive:</p>
            <ul>
              <li>Lifetime access to the Content Commander Challenge</li>
              <li v-if="includeUpsell">Lifetime access to the Full AI Captains Academy (all 9 courses)</li>
              <li>Your API key via email (shortly after purchase)</li>
            </ul>
          </div>
          
          <div class="button-group">
            <button class="secondary-button" @click="$emit('cancel')">
              Cancel
            </button>
            <button class="cta-button" @click="$emit('confirm')" :disabled="loading">
              <span v-if="loading">
                <span class="spinner"></span>
                Processing...
              </span>
              <span v-else>
                <i class="fas fa-check-circle"></i> Complete Purchase
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConfirmationModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      required: true
    },
    includeUpsell: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    basePrice() {
      // Get price from environment variable with a fallback
      return parseInt(import.meta.env.VITE_CONTENT_COMMANDER_PRICE) || 2900;
    },
    upsellPrice() {
      // Get upsell price from environment variable with a fallback
      return parseInt(import.meta.env.VITE_ACADEMY_UPSELL_PRICE) || 24900;
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #2d2d2d;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #3d3d3d;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  padding: 0 30px;
  text-align: center;
}

.modal-header h3 {
  color: #FFD23F;
  margin: 0;
  font-size: 1.5rem;
  width: 100%;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  color: #e0e0e0;
  position: absolute;
  right: -10px;
  top: -10px;
  z-index: 2;
  transition: color 0.2s;
}

.close-button:hover {
  color: #FFD23F;
}

.confirmation-content {
  text-align: center;
}

.order-details {
  background: #363636;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border: 1px solid #3d3d3d;
}

.product-row {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  color: #e0e0e0;
  text-align: left;
}

.upsell-row {
  border-top: 1px dashed #3d3d3d;
  padding-top: 1rem;
  margin-top: 0.5rem;
}

.product-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
}

.product-name i {
  color: #FFD23F;
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #3d3d3d;
  color: #e0e0e0;
  font-size: 1.1rem;
}

.confirmation-message {
  margin: 1.5rem 0;
  text-align: left;
  color: #e0e0e0;
}

.confirmation-message p {
  margin-bottom: 1rem;
}

.confirmation-message ul {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
}

.confirmation-message li {
  margin-bottom: 0.5rem;
}

.button-group {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cta-button {
  flex: 1;
  padding: 1rem;
  background: #FFD23F;
  color: #1a1a1a;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: bold;
}

.cta-button:hover {
  background: ##FFD23F;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
}

.cta-button:disabled {
  background: #666;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.secondary-button {
  flex: 0.7;
  padding: 1rem;
  background: #666;
  color: #e0e0e0;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.secondary-button:hover {
  background: #555;
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(26, 26, 26, 0.3);
  border-radius: 50%;
  border-top-color: #1a1a1a;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .button-group {
    flex-direction: column;
  }
  
  .cta-button, .secondary-button {
    width: 100%;
  }
}
</style> 